// pages/Careers.js
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Work,
  LocationOn,
  Schedule,
  AttachMoney,
  Send,
  Close,
  CheckCircle,
  FilterList,
  Category
} from '@mui/icons-material';

const Careers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedJob, setSelectedJob] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applicationStep, setApplicationStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [activeTab, setActiveTab] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: '',
    resume: null
  });

  // Job data
  const openPositions = [
    {
      id: 1,
      title: 'Senior Full Stack Developer',
      department: 'Engineering',
      type: 'Full-time',
      location: 'Remote',
      experience: '3-5 years',
      salary: '₹8L - ₹15L',
      description: 'We are looking for an experienced Full Stack Developer to join our growing engineering team.',
      skills: ['React', 'Node.js', 'MongoDB', 'AWS', 'Docker'],
      urgent: true
    },
    {
      id: 2,
      title: 'UI/UX Designer',
      department: 'Design',
      type: 'Full-time',
      location: 'Hybrid',
      experience: '2-4 years',
      salary: '₹6L - ₹12L',
      description: 'Join our design team to create beautiful, intuitive user experiences for our clients.',
      skills: ['Figma', 'UI/UX', 'Prototyping', 'User Research'],
      featured: true
    },
    {
      id: 3,
      title: 'DevOps Engineer',
      department: 'Engineering',
      type: 'Full-time',
      location: 'Remote',
      experience: '2-5 years',
      salary: '₹7L - ₹14L',
      description: 'We need a DevOps Engineer to streamline our development processes and cloud infrastructure.',
      skills: ['AWS', 'Docker', 'Kubernetes', 'CI/CD']
    },
    {
      id: 4,
      title: 'IT Consultant',
      department: 'Consulting',
      type: 'Full-time',
      location: 'On-site',
      experience: '3-6 years',
      salary: '₹9L - ₹16L',
      description: 'Provide expert IT consulting services for digital transformation projects.',
      skills: ['IT Strategy', 'Digital Transformation', 'Cybersecurity']
    },
    {
      id: 5,
      title: 'Frontend Developer Intern',
      department: 'Engineering',
      type: 'Internship',
      location: 'Remote',
      experience: '0-1 years',
      salary: 'Stipend + Certificate',
      description: 'Great opportunity for students to learn and grow with our team.',
      skills: ['HTML', 'CSS', 'JavaScript', 'React'],
      internship: true
    },
    {
      id: 6,
      title: 'Digital Marketing Specialist',
      department: 'Marketing',
      type: 'Full-time',
      location: 'Hybrid',
      experience: '2-4 years',
      salary: '₹5L - ₹10L',
      description: 'Drive our digital marketing efforts and grow our brand presence.',
      skills: ['SEO', 'Social Media', 'Google Analytics', 'Content Marketing']
    }
  ];

  // Benefits data
  const perks = [
    {
      icon: AttachMoney,
      title: 'Competitive Salary',
      description: 'Industry-leading compensation with performance bonuses'
    },
    {
      icon: Work,
      title: 'Flexible Work',
      description: 'Remote options and flexible scheduling'
    },
    {
      icon: Category,
      title: 'Career Growth',
      description: 'Clear growth paths and promotion opportunities'
    }
  ];

  // Hiring process
  const hiringProcess = [
    {
      step: 1,
      title: 'Application Review',
      description: 'We carefully review every application within 48 hours'
    },
    {
      step: 2,
      title: 'Screening Call',
      description: '30-minute call to discuss your background and interests'
    },
    {
      step: 3,
      title: 'Technical Assessment',
      description: 'Practical skills test relevant to the position'
    },
    {
      step: 4,
      title: 'Final Interview',
      description: 'Meet the team and discuss your approach'
    },
    {
      step: 5,
      title: 'Offer',
      description: 'Welcome to Zolang Nexora!'
    }
  ];

  // Filter jobs based on search and filters
  const filteredJobs = openPositions.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDepartment = selectedDepartment === 'all' || job.department === selectedDepartment;
    const matchesType = selectedType === 'all' || job.type === selectedType;

    return matchesSearch && matchesDepartment && matchesType;
  });

  // Unique departments and types for filters
  const departments = [...new Set(openPositions.map(job => job.department))];
  const jobTypes = [...new Set(openPositions.map(job => job.type))];

  const handleApply = (job) => {
    setSelectedJob(job);
    setFormData(prev => ({ ...prev, position: job.title }));
    setApplicationStep(0);
    setApplyDialogOpen(true);
  };

  const handleNextStep = () => setApplicationStep(prev => prev + 1);
  const handlePrevStep = () => setApplicationStep(prev => prev - 1);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    console.log('Application submitted:', formData);
    handleNextStep();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedDepartment('all');
    setSelectedType('all');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1128 0%, #0f1a3a 30%, #1a2342 100%)',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <br /><br />
            <Typography
              variant="h1"
              sx={{
                fontWeight: '900',
                background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 2
              }}
            >
              Join Our Team
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                mb: 4,
                maxWidth: '600px',
                margin: '0 auto'
              }}
            >
              Build amazing digital experiences while growing your career with us.
            </Typography>
            <Typography
              sx={{
                mt: 1.5,
                fontSize: "0.85rem",
                color: "rgba(255,255,255,0.65)",
                fontStyle: "italic",
                textAlign: "center",
                letterSpacing: "0.03em"
              }}
            >
              “முயற்சி திருவினை ஆக்கும்”
            </Typography>

            <Typography
              sx={{
                mt: 0.4,
                fontSize: "0.75rem",
                color: "rgba(255,255,255,0.55)",
                textAlign: "center"
              }}
            >
              “Persistent effort creates success.”
            </Typography>

          </motion.div>
        </Box>

        {/* Tabs Section */}
        <Card sx={{ mb: 6, background: 'rgba(139,0,0,0.1)', border: '1px solid rgba(139,0,0,0.3)' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            centered
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.7)',
                fontWeight: '600',
                fontSize: '1rem'
              },
              '& .Mui-selected': {
                color: '#ff4d4d'
              }
            }}
          >
            <Tab label="Open Positions" />
            <Tab label="Hiring Process" />
            <Tab label="Why Join Us" />
          </Tabs>
        </Card>

        {/* Tab Content */}
        {activeTab === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Search and Filters */}
            <Card sx={{ p: 3, mb: 4, background: 'rgba(139,0,0,0.1)', border: '1px solid rgba(139,0,0,0.3)' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search jobs or skills..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#ff4d4d' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                        '&:hover fieldset': { borderColor: '#ff4d4d' },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Department</InputLabel>
                    <Select
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                      label="Department"
                      sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff4d4d' },
                      }}
                    >
                      <MenuItem value="all">All Departments</MenuItem>
                      {departments.map(dept => (
                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Job Type</InputLabel>
                    <Select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      label="Job Type"
                      sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff4d4d' },
                      }}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {jobTypes.map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    onClick={resetFilters}
                    startIcon={<FilterList />}
                    sx={{
                      border: '1px solid rgba(139,0,0,0.5)',
                      color: 'rgba(255,255,255,0.8)',
                      height: '56px'
                    }}
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            </Card>

            {/* Jobs Grid */}
            <Grid container spacing={3}>
              {filteredJobs.map((job, index) => (
                <Grid item xs={12} key={job.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card
                      sx={{
                        background: 'linear-gradient(135deg, rgba(139,0,0,0.1) 0%, rgba(139,0,0,0.05) 100%)',
                        border: job.urgent ? '2px solid #ff4d4d' : '1px solid rgba(139,0,0,0.3)',
                        borderRadius: 2,
                        p: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(139, 0, 0, 0.2)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h5" sx={{ fontWeight: '700', color: 'white' }}>
                              {job.title}
                            </Typography>
                            {job.urgent && <Chip label="Urgent" color="error" size="small" />}
                            {job.internship && <Chip label="Internship" color="primary" size="small" />}
                          </Box>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Work sx={{ fontSize: 16, color: '#ff4d4d' }} />
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {job.department}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Schedule sx={{ fontSize: 16, color: '#ff4d4d' }} />
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {job.type}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn sx={{ fontSize: 16, color: '#ff4d4d' }} />
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {job.location}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AttachMoney sx={{ fontSize: 16, color: '#ff4d4d' }} />
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {job.salary}
                              </Typography>
                            </Box>
                          </Box>

                          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
                            {job.description}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {job.skills.map((skill, idx) => (
                              <Chip
                                key={idx}
                                label={skill}
                                size="small"
                                sx={{
                                  background: 'rgba(139,0,0,0.3)',
                                  color: 'rgba(255,255,255,0.9)',
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        <Button
                          variant="contained"
                          onClick={() => handleApply(job)}
                          sx={{
                            background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
                            color: 'white',
                            fontWeight: '600',
                            px: 4,
                            minWidth: '140px',
                            height: 'fit-content'
                          }}
                        >
                          Apply Now
                        </Button>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {filteredJobs.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  No positions found matching your criteria.
                </Typography>
                <Button onClick={resetFilters} sx={{ mt: 2, color: '#ff4d4d' }}>
                  Reset filters
                </Button>
              </Box>
            )}
          </motion.div>
        )}

        {activeTab === 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ p: 4, background: 'rgba(139,0,0,0.1)', border: '1px solid rgba(139,0,0,0.3)' }}>
              <Typography variant="h4" sx={{ color: '#ff4d4d', mb: 4, textAlign: 'center' }}>
                Our Hiring Process
              </Typography>

              <Stepper orientation={isMobile ? "vertical" : "horizontal"} activeStep={-1}>
                {hiringProcess.map((step) => (
                  <Step key={step.step}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            background: 'linear-gradient(135deg, #8b0000, #ff4d4d)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem'
                          }}
                        >
                          {step.step}
                        </Box>
                      )}
                    >
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: '600' }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {step.description}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Card>
          </motion.div>
        )}

        {activeTab === 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Grid container spacing={3}>
              {perks.map((perk, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, rgba(139,0,0,0.1) 0%, rgba(139,0,0,0.05) 100%)',
                      border: '1px solid rgba(139,0,0,0.3)',
                      borderRadius: 2,
                      p: 3,
                      height: '100%',
                      textAlign: 'center'
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 60,
                          height: 60,
                          background: 'linear-gradient(135deg, #8b0000, #ff4d4d)',
                          borderRadius: '50%',
                          mb: 2
                        }}
                      >
                        <perk.icon sx={{ fontSize: 30, color: 'white' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: '700', color: 'white', mb: 1 }}>
                        {perk.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {perk.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        )}

        {/* Application Dialog */}
        <Dialog
          open={applyDialogOpen}
          onClose={() => setApplyDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, #0a1128 0%, #1a2342 100%)',
              border: '2px solid rgba(139,0,0,0.4)'
            }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: '700', color: '#ff4d4d' }}>
                Apply for {selectedJob?.title}
              </Typography>
              <Button onClick={() => setApplyDialogOpen(false)} sx={{ color: '#ff4d4d', minWidth: 'auto' }}>
                <Close />
              </Button>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Stepper activeStep={applicationStep} sx={{ mb: 4 }}>
              <Step><StepLabel>Personal Info</StepLabel></Step>
              <Step><StepLabel>Professional Details</StepLabel></Step>
              <Step><StepLabel>Review & Submit</StepLabel></Step>
            </Stepper>

            <AnimatePresence mode="wait">
              {applicationStep === 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&:hover fieldset': { borderColor: '#ff4d4d' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&:hover fieldset': { borderColor: '#ff4d4d' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&:hover fieldset': { borderColor: '#ff4d4d' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                        }}
                      />
                    </Grid>
                  </Grid>
                </motion.div>
              )}

              {applicationStep === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Experience</InputLabel>
                        <Select
                          value={formData.experience}
                          onChange={(e) => handleInputChange('experience', e.target.value)}
                          label="Experience"
                          sx={{
                            color: 'white',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff4d4d' },
                          }}
                        >
                          <MenuItem value="0-1">0-1 years</MenuItem>
                          <MenuItem value="1-3">1-3 years</MenuItem>
                          <MenuItem value="3-5">3-5 years</MenuItem>
                          <MenuItem value="5+">5+ years</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Cover Letter"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&:hover fieldset': { borderColor: '#ff4d4d' },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                        }}
                      />
                    </Grid>
                  </Grid>
                </motion.div>
              )}

              {applicationStep === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CheckCircle sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: '600', mb: 2 }}>
                      Ready to Submit!
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 3 }}>
                      Review your application for {selectedJob?.title}
                    </Typography>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={applicationStep === 0 ? () => setApplyDialogOpen(false) : handlePrevStep}
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              {applicationStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              onClick={applicationStep === 2 ? handleFormSubmit : handleNextStep}
              variant="contained"
              endIcon={applicationStep === 2 ? <Send /> : null}
              sx={{
                background: 'linear-gradient(45deg, #8b0000, #ff4d4d)',
              }}
            >
              {applicationStep === 2 ? 'Submit' : 'Next'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Careers;